/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { FiMail, FiPhone, FiCalendar } from 'react-icons/fi';
import add from 'date-fns/add';
import AppDatePicker from '../../../../shared/components/app-date-picker/AppDatePicker';
import {
  getNewDate,
  getAPIFormattedDate,
  isNullOrEmpty,
} from '../../../../shared/helper-utils/appUtils';
import './LeadContactPopup.scss';
import { TODAY_DATE } from '../../../../shared/constants/Constants';
import { countryMobileCodes } from '../../../../shared/constants/CountryOptions';
import { isValidDate, isValidMobileNumber } from '../../utils/onlineVisaUtils';

function isoFromDialCode(dialCode) {
  const match = countryMobileCodes.find(
    (c) => c.dial_code === (dialCode || '+91'),
  );
  return match?.code || 'IN';
}

/**
 * Small modal that collects the lead pax contact + travel dates before
 * the booking form is rendered. The user can close/skip it; the icon
 * in the sidebar re-opens the same popup for edits.
 */
function LeadContactPopup({
  show,
  onHide,
  initialValues,
  onContinue,
  minTravelDate,
  stayDuration,
}) {
  console.log(initialValues);
  const [form, setForm] = useState({
    travelDate: '',
    returnDate: '',
    email: '',
    phone: '',
    countryCode: '+91',
    countryIso: 'IN',
    confirm: false,
  });
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (show) {
      const dialCode = initialValues?.countryCode || '+91';
      setForm({
        travelDate: initialValues?.travelDate || '',
        returnDate: initialValues?.returnDate || '',
        email: initialValues?.email || '',
        phone: initialValues?.phone || '',
        countryCode: dialCode,
        countryIso: isoFromDialCode(dialCode),
        confirm: !!initialValues?.confirm,
      });
      setTouched(false);
    }
  }, [show, initialValues]);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneValid = isValidMobileNumber(
    String(form.phone || '').trim(),
    form.countryIso,
  );
  const emailValid = emailRegex.test(String(form.email || '').trim());
  const travelValid = !!form.travelDate;
  const confirmValid = !!form.confirm;

  const handleCountryChange = (e) => {
    const iso = e.target.value;
    const found = countryMobileCodes.find((c) => c.code === iso);
    setForm((p) => ({
      ...p,
      countryIso: iso,
      countryCode: found?.dial_code || '',
    }));
  };

  const handleSubmit = () => {
    setTouched(true);
    if (!travelValid || !emailValid || !phoneValid || !confirmValid) return;
    onContinue({
      travelDate: form.travelDate,
      returnDate: form.returnDate,
      email: String(form.email || '').trim(),
      phone: String(form.phone || '').trim(),
      countryCode: form.countryCode,
      confirm: true,
    });
  };

  const minDate =
    !isNullOrEmpty(minTravelDate) && minTravelDate !== 0
      ? add(getNewDate(TODAY_DATE), { days: Number(minTravelDate) })
      : getNewDate(TODAY_DATE);

  const maxReturnDate = useMemo(() => {
    if (
      !isNullOrEmpty(stayDuration) &&
      !isNullOrEmpty(form.travelDate) &&
      stayDuration !== 0
    ) {
      if (isValidDate(getNewDate(form.travelDate))) {
        return add(getNewDate(form.travelDate), { days: stayDuration });
      }
    }
    return '';
  }, [form.travelDate]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="lead-contact-popup"
    >
      <Modal.Header closeButton>
        <Modal.Title className="lead-contact-popup__title">
          Lead Passenger Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <section className="lead-contact-popup__section">
          <div className="lead-contact-popup__section-head">
            <FiCalendar />
            <span>Travelling Date</span>
          </div>
          <div className="lead-contact-popup__date-row">
            <div className="lead-contact-popup__date-col">
              <AppDatePicker
                name="travelDate"
                label="Travel Date"
                placeholder=""
                required
                className="app-date-picker"
                labelClassName="booking-date-label"
                minDate={minDate}
                selectedDate={
                  form.travelDate ? getNewDate(form.travelDate) : ''
                }
                onChange={(date) =>
                  setForm((p) => ({
                    ...p,
                    travelDate: getAPIFormattedDate(date),
                    returnDate: '',
                  }))
                }
                customClassName={`position-relative flight-booking-picker ${
                  touched && !travelValid ? 'app-error-text-border' : ''
                }`}
              />
              {touched && !travelValid && (
                <small className="lead-contact-popup__err">
                  Please pick your travel date.
                </small>
              )}
            </div>
            <div className="lead-contact-popup__date-col">
              <AppDatePicker
                name="returnDate"
                label="Return Date"
                required
                placeholder=""
                className="app-date-picker"
                labelClassName="booking-date-label"
                minDate={
                  form.travelDate ? getNewDate(form.travelDate) : minDate
                }
                selectedDate={
                  form.returnDate ? getNewDate(form.returnDate) : ''
                }
                maxDate={maxReturnDate}
                onChange={(date) =>
                  setForm((p) => ({
                    ...p,
                    returnDate: getAPIFormattedDate(date),
                  }))
                }
                customClassName={`position-relative flight-booking-picker ${
                  touched && isNullOrEmpty(form.returnDate)
                    ? 'app-error-text-border'
                    : ''
                }`}
              />
              {touched && isNullOrEmpty(form.returnDate) && (
                <small className="lead-contact-popup__err">
                  Please pick your Return date.
                </small>
              )}
            </div>
          </div>
        </section>

        <section className="lead-contact-popup__section">
          <div className="lead-contact-popup__section-head">
            <FiMail />
            <span>Contact Details</span>
          </div>

          <div className="lead-contact-popup__field">
            <label
              htmlFor="lead-email"
              className="lead-contact-popup__label required"
            >
              Email Address
            </label>
            <div className="lead-contact-popup__input-wrap">
              <FiMail className="lead-contact-popup__input-icon" />
              <input
                id="lead-email"
                type="email"
                className={`lead-contact-popup__input ${
                  touched && !emailValid
                    ? 'lead-contact-popup__input--error'
                    : ''
                }`}
                placeholder="name@example.com"
                value={form.email}
                onChange={(e) =>
                  setForm((p) => ({ ...p, email: e.target.value }))
                }
              />
            </div>
            {touched && !emailValid && (
              <small className="lead-contact-popup__err">
                Enter a valid email address.
              </small>
            )}
          </div>

          <div className="lead-contact-popup__field">
            <label
              htmlFor="lead-phone"
              className="lead-contact-popup__label required"
            >
              Mobile Number
            </label>
            <div className="lead-contact-popup__phone-row">
              <select
                className={`lead-contact-popup__cc-select ${
                  touched && !phoneValid
                    ? 'lead-contact-popup__input--error'
                    : ''
                }`}
                value={form.countryIso}
                onChange={handleCountryChange}
                aria-label="Country code"
              >
                {countryMobileCodes.map((c) => (
                  <option key={`${c.code}-${c.dial_code}`} value={c.code}>
                    {c.name} ({c.dial_code})
                  </option>
                ))}
              </select>
              <div className="lead-contact-popup__input-wrap lead-contact-popup__input-wrap--grow">
                <FiPhone className="lead-contact-popup__input-icon" />
                <input
                  id="lead-phone"
                  type="tel"
                  inputMode="numeric"
                  className={`lead-contact-popup__input ${
                    touched && !phoneValid
                      ? 'lead-contact-popup__input--error'
                      : ''
                  }`}
                  placeholder="9876543210"
                  value={form.phone}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      phone: String(e.target.value).replace(/[^0-9]/g, ''),
                    }))
                  }
                />
              </div>
            </div>
            {touched && !phoneValid && (
              <small className="lead-contact-popup__err">
                Enter a valid mobile number
              </small>
            )}
          </div>

          <label className="lead-contact-popup__checkbox">
            <input
              type="checkbox"
              checked={form.confirm}
              onChange={(e) =>
                setForm((p) => ({ ...p, confirm: e.target.checked }))
              }
            />
            <span>
              I confirm these are the traveler&apos;s contact details for
              booking updates.
            </span>
          </label>
          {touched && !confirmValid && (
            <small className="lead-contact-popup__err">
              Please confirm the contact details.
            </small>
          )}
        </section>
      </Modal.Body>
      <Modal.Footer className="lead-contact-popup__footer">
        <button
          type="button"
          className="lead-contact-popup__skip"
          onClick={onHide}
        >
          Skip for now
        </button>
        <button
          type="button"
          className="lead-contact-popup__continue"
          onClick={handleSubmit}
        >
          Continue
        </button>
      </Modal.Footer>
    </Modal>
  );
}

LeadContactPopup.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  onContinue: PropTypes.func.isRequired,
  minTravelDate: PropTypes.any,
  stayDuration: PropTypes.any.isRequired,
};

LeadContactPopup.defaultProps = {
  initialValues: null,
  minTravelDate: '',
};

export default LeadContactPopup;
